import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ShieldLogo from '../components/ShieldLogo';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [waitlistData, setWaitlistData] = useState([]);
  const [funnelData, setFunnelData] = useState(null);
  const [bentoData, setBentoData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`${API}/waitlist/all`, {
        headers: {
          'Authorization': `Bearer ${password}`
        }
      });

      if (response.data.success) {
        setIsAuthenticated(true);
        setWaitlistData(response.data.data);
        // Fetch funnel data in parallel (fail-soft — empty section if it errors)
        axios
          .get(`${API}/admin/funnel`, { headers: { Authorization: `Bearer ${password}` } })
          .then((r) => setFunnelData(r.data))
          .catch(() => setFunnelData({ steps: [], error: true }));
        // Fetch hero bento taps breakdown in parallel
        axios
          .get(`${API}/admin/hero-bento-taps`, { headers: { Authorization: `Bearer ${password}` } })
          .then((r) => setBentoData(r.data))
          .catch(() => setBentoData({ cards: [], error: true }));
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Invalid password');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'Instrument Serif, serif' }}>
              Verilock Admin
            </h1>
            <p className="text-[#1a1a1a]/60">Enter password to view waitlist</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}
            
            <Input
              type="password"
              placeholder="Admin password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              className="bg-white border-[#e0ddd8]"
              required
              autoFocus
            />
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1a1a1a] text-white hover:bg-[#2a2a2a] transition-colors"
            >
              {loading ? 'Verifying...' : 'Access Admin'}
            </Button>
          </form>

          <button
            onClick={() => navigate('/')}
            className="mt-6 text-sm text-[#1a1a1a]/60 hover:text-[#1a1a1a] transition-colors w-full text-center"
          >
            ← Back to home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-[#e0ddd8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-2">
              <ShieldLogo size={22} />
              <h1 className="text-xl sm:text-2xl font-semibold leading-tight" style={{ fontFamily: 'Instrument Serif, serif' }}>
                Verilock Admin
              </h1>
            </div>
            <p className="text-sm text-[#1a1a1a]/60 mt-1">
              {waitlistData.length} {waitlistData.length === 1 ? 'signup' : 'signups'}
            </p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="text-sm text-[#1a1a1a]/60 hover:text-[#1a1a1a] transition-colors h-11 px-3 rounded-md whitespace-nowrap"
          >
            ← Home
          </button>
        </div>
      </div>

      {/* Funnel + Waitlist */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-10">
        <FunnelSection data={funnelData} />
        <HeroBentoSection data={bentoData} />

        {waitlistData.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-[#1a1a1a]/60 text-lg">No signups yet</p>
          </div>
        ) : (
          <div className="bg-white border border-[#e0ddd8] rounded-xl overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead className="bg-[#f5f3ef] border-b border-[#e0ddd8]">
                <tr>
                  <th className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-[#1a1a1a]">
                    Name
                  </th>
                  <th className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-[#1a1a1a]">
                    Email
                  </th>
                  <th className="px-4 sm:px-6 py-4 text-left text-sm font-semibold text-[#1a1a1a]">
                    Date Signed Up
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e0ddd8]">
                {waitlistData.map((entry, idx) => (
                  <tr key={idx} className="hover:bg-[#f5f3ef]/30 transition-colors">
                    <td className="px-4 sm:px-6 py-4 text-sm text-[#1a1a1a]">
                      {entry.name}
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm text-[#1a1a1a] break-all">
                      {entry.email}
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm text-[#1a1a1a]/60 whitespace-nowrap">
                      {formatDate(entry.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;

/* -------------------- Funnel section -------------------- */

const STEP_LABELS = {
  wizard_started: 'Wizard started',
  wizard_step_2: 'Wizard step 2',
  wizard_step_3: 'Wizard step 3',
  wizard_step_4: 'Wizard step 4',
  wizard_step_5: 'Wizard step 5',
  wizard_step_6: 'Wizard step 6 (email)',
  wizard_submitted: 'Wizard submitted',
  results_generated: 'Results generated',
  payment_page_viewed: 'Payment page viewed',
  payment_completed: 'Payment completed',
  dashboard_accessed: 'Dashboard accessed',
};

const FunnelSection = ({ data }) => {
  if (!data) {
    return (
      <div className="bg-white border border-[#e0ddd8] rounded-xl p-6">
        <h2 className="text-lg font-semibold text-[#1a1a1a]" style={{ fontFamily: 'Instrument Serif, serif' }}>
          Conversion funnel
        </h2>
        <p className="text-sm text-[#1a1a1a]/55 mt-2">Loading funnel data...</p>
      </div>
    );
  }

  const steps = data.steps || [];
  const top = Math.max(...steps.map((s) => s.count || 0), 1);

  return (
    <div
      data-testid="admin-funnel-section"
      className="bg-white border border-[#e0ddd8] rounded-xl p-5 sm:p-6"
    >
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-xl sm:text-2xl font-normal text-[#1a1a1a]" style={{ fontFamily: 'Instrument Serif, serif' }}>
          Conversion funnel
        </h2>
        <span className="text-xs text-[#1a1a1a]/55">{steps.length} steps</span>
      </div>

      {steps.length === 0 ? (
        <p className="text-sm text-[#1a1a1a]/55 py-6 text-center">No funnel data yet.</p>
      ) : (
        <ul className="space-y-2">
          {steps.map((s) => {
            const label = STEP_LABELS[s.event] || s.event;
            const widthPct = top > 0 ? Math.max((s.count / top) * 100, s.count > 0 ? 4 : 0) : 0;
            const drop = s.drop_from_prev_pct;
            return (
              <li
                key={s.event}
                data-testid={`funnel-row-${s.event}`}
                className="flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-2.5 rounded-lg hover:bg-[#f5f3ef]/40 transition-colors"
              >
                <div className="w-40 sm:w-56 text-sm text-[#1a1a1a] truncate">{label}</div>
                <div className="flex-1 relative h-7 bg-[#f5f3ef] rounded-md overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-[#0a5c44] transition-all duration-500"
                    style={{ width: `${widthPct}%` }}
                  />
                  <span className="absolute inset-0 flex items-center px-2.5 text-xs font-semibold text-white mix-blend-difference">
                    {s.count.toLocaleString()}
                  </span>
                </div>
                <div className="w-20 text-right text-xs text-[#1a1a1a]/55 whitespace-nowrap">
                  {s.unique ? `${s.unique} uniq` : '—'}
                </div>
                <div className="w-16 text-right text-xs whitespace-nowrap">
                  {drop == null ? (
                    <span className="text-[#1a1a1a]/40">—</span>
                  ) : drop <= 0 ? (
                    <span className="text-[#0a5c44]">+{Math.abs(drop)}%</span>
                  ) : (
                    <span className="text-red-700">-{drop}%</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <p className="text-[11px] text-[#1a1a1a]/45 mt-4">
        Drop-off shown vs. previous step. "uniq" = distinct sessions.
      </p>
    </div>
  );
};

/* -------------------- Hero bento taps section -------------------- */

const HeroBentoSection = ({ data }) => {
  if (!data) {
    return (
      <div className="bg-white border border-[#e0ddd8] rounded-xl p-6">
        <h2
          className="text-lg font-semibold text-[#1a1a1a]"
          style={{ fontFamily: 'Instrument Serif, serif' }}
        >
          Hero bento taps
        </h2>
        <p className="text-sm text-[#1a1a1a]/55 mt-2">Loading bento data...</p>
      </div>
    );
  }

  const cards = data.cards || [];

  return (
    <div
      data-testid="admin-hero-bento-section"
      className="bg-white border border-[#e0ddd8] rounded-xl p-5 sm:p-6"
    >
      <div className="flex items-baseline justify-between mb-4">
        <h2
          className="text-xl sm:text-2xl font-normal text-[#1a1a1a]"
          style={{ fontFamily: 'Instrument Serif, serif' }}
        >
          Hero bento taps
        </h2>
        <span className="text-xs text-[#1a1a1a]/55">
          {(data.total ?? 0).toLocaleString()} total
        </span>
      </div>

      {cards.length === 0 ? (
        <p className="text-sm text-[#1a1a1a]/55 py-6 text-center">
          No bento taps yet.
        </p>
      ) : (
        <ul className="space-y-2">
          {cards.map((c) => (
            <li
              key={c.card_title}
              data-testid={`bento-row-${c.card_title.toLowerCase().replace(/\s+/g, '-')}`}
              className="flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-2.5 rounded-lg hover:bg-[#f5f3ef]/40 transition-colors"
            >
              <div className="w-40 sm:w-56 text-sm text-[#1a1a1a] truncate">
                {c.card_title}
              </div>
              <div className="flex-1 text-sm font-semibold text-[#0a5c44]">
                {c.count.toLocaleString()}
              </div>
              <div className="w-20 text-right text-xs text-[#1a1a1a]/55 whitespace-nowrap">
                {c.unique ? `${c.unique} uniq` : '—'}
              </div>
            </li>
          ))}
        </ul>
      )}

      <p
        data-testid="admin-hero-bento-summary"
        className="text-[11px] text-[#1a1a1a]/55 mt-4 leading-relaxed"
      >
        {cards
          .filter((c) => ['Compliance Score', 'Trust Page', 'Policy Templates'].includes(c.card_title))
          .map((c) => `${c.card_title}: ${c.count.toLocaleString()}`)
          .join(' · ')}
      </p>
    </div>
  );
};
